/* eslint-disable @typescript-eslint/no-explicit-any */
import ReactECharts from 'echarts-for-react';
import type { EChartsOption } from 'echarts';
import { PRODGY_CHART_COLORS } from '@/shared/constants/colors';
import { useChartTheme } from './chartTheme';

interface GaugeMultipleChartProps {
  data: Record<string, any>;
  config: {
    max?: number;
    thresholds?: {
      good?: number;
      warning?: number;
      danger?: number;
    };
  };
}

export const GaugeMultipleChart = ({ data, config }: GaugeMultipleChartProps) => {
  const chart = useChartTheme();

  const dataKeys = Object.keys(data);
  const maxValue = config.max || Math.max(...dataKeys.map(key => data[key]?.max || 90));
  const thresholds = config.thresholds || null;

  // Gauge de escala numérica com barra segmentada (acima de todos os arcos)
  const scaleGauge = {
    type: 'gauge',
    startAngle: 180,
    endAngle: 0,
    min: 0,
    max: maxValue,
    radius: '106%',
    center: ['50%', '70%'],
    splitNumber: 5,
    axisLine: {
      show: true,
      lineStyle: {
        width: 5,
        color: [
          [0.2, chart.trackSoft],
          [0.4, chart.track],
          [0.6, chart.trackSoft],
          [0.8, chart.track],
          [1, chart.trackSoft],
        ],
      },
    },
    pointer: { show: false },
    axisTick: {
      show: false,
    },
    splitLine: {
      show: true,
      distance: -6,
      length: 8,
      lineStyle: {
        color: chart.axisLine,
        width: 2,
      },
    },
    axisLabel: {
      show: true,
      distance: -20,
      color: chart.axisLabel,
      fontSize: 10,
      formatter: (value: number) => value.toFixed(0),
    },
    detail: { show: false },
    title: { show: false },
    data: [],
  };

  // VELOCÍMETRO: Gauge de fundo com zonas de threshold (reduzido)
  const backgroundGauge = {
    type: 'gauge',
    startAngle: 180,
    endAngle: 0,
    min: 0,
    max: maxValue,
    radius: '51%',
    center: ['50%', '70%'],
    splitNumber: 5,
    axisLine: {
      lineStyle: {
        width: 7,
        // Rampa neutra do claro ao escuro — de propósito: as zonas são pano de
        // fundo, e é o arco colorido do valor que precisa se destacar sobre elas.
        color: thresholds ? [
          [(thresholds.good || 0) / maxValue, chart.axisLine],
          [(thresholds.warning || 0) / maxValue, chart.track],
          [(thresholds.danger || maxValue) / maxValue, chart.deep],
          [1, chart.deep],
        ] : [[1, chart.track]],
      },
    },
    pointer: { show: false },
    axisTick: {
      show: false,
    },
    splitLine: {
      show: false,
    },
    axisLabel: {
      show: false,
    },
    detail: { show: false },
    title: { show: false },
    data: [],
  };

  // Barras em arco para cada métrica (acima do threshold)
  const valueBars = dataKeys.map((key, index) => {
    const gaugeData = data[key];
    const value = gaugeData?.value || 0;
    const name = gaugeData?.name || key;
    const color = PRODGY_CHART_COLORS[index % PRODGY_CHART_COLORS.length];

    // Barras em camadas acima do threshold com espaçamento uniforme de 22
    const radiusPercent = 73 + (index * 22);

    return {
      type: 'gauge',
      name: name,
      startAngle: 180,
      endAngle: 0,
      min: 0,
      max: maxValue,
      radius: `${radiusPercent}%`,
      center: ['50%', '70%'],
      splitNumber: 5,
      progress: {
        show: true,
        width: 19,
        itemStyle: {
          color: color,
        },
      },
      axisLine: {
        show: true,
        lineStyle: {
          width: 19,
          color: [[1, chart.trackFaint]],
        },
      },
      axisTick: { show: false },
      splitLine: { show: false },
      axisLabel: { show: false },
      pointer: { show: false },
      detail: { show: false },
      title: { show: false },
      data: [{ value, name: `${name}: ${value.toFixed(1)} days` }],
    };
  });

  const option: EChartsOption = {
    backgroundColor: chart.background,
    textStyle: { color: chart.axisLabel },
    tooltip: {
      trigger: 'item',
      formatter: (params: any) => {
        if (params.seriesType === 'gauge' && params.data?.name) {
          return params.data.name;
        }
        return '';
      },
      backgroundColor: chart.tooltipBg,
      borderColor: chart.tooltipBorder,
      textStyle: {
        color: chart.tooltipText,
      },
    },
    legend: {
      show: true,
      bottom: '0%',
      textStyle: {
        color: chart.legendText,
        fontSize: 12,
      },
      data: dataKeys.map((key, index) => {
        const gaugeData = data[key];
        const name = gaugeData?.name || key;
        const color = PRODGY_CHART_COLORS[index % PRODGY_CHART_COLORS.length];
        
        return {
          name: name,
          itemStyle: {
            color: color,
          },
        };
      }),
    },
    series: [
      scaleGauge,
      backgroundGauge,
      ...valueBars,
    ] as any,
  };

  return (
    <ReactECharts
      option={option}
      style={{ height: '100%', width: '100%' }}
      notMerge={true}
      lazyUpdate={true}
    />
  );
};
